/**
 * Pill surface selection, shared by web and native.
 *
 * The RULE is shared; the VALUE is not. Web resolves surfaces to CSS custom
 * properties and native to hex constants, so this returns a semantic role and
 * each platform maps it through its own token layer. Sharing the resolved
 * color would force one platform's representation onto the other; sharing the
 * rule prevents the platforms from disagreeing about *when* a pill is
 * emphasized.
 *
 * RAI-1091: both platforms independently used the app background (#131314)
 * for unselected pills, one step off the canvas, which made them read as
 * invisible outlines. Both were "fixed" separately in the same change — the
 * duplication that made the original divergence possible.
 */

export const E_PillSurface = {
  /** Emphasized surface: accent tone or an active selection. */
  ACCENT: "accent",
  /** Resting surface: the chat-kit chip background. */
  CHIP: "chip",
} as const;

export type T_PillSurface = (typeof E_PillSurface)[keyof typeof E_PillSurface];

export type T_PillTone = "accent" | "neutral";

/**
 * Which surface role a pill should render.
 *
 * Selection implies emphasis: a selected neutral pill reads as active, which
 * is why `selected` and `tone: "accent"` collapse to the same surface.
 */
export function resolvePillSurface(args: {
  selected?: boolean;
  tone?: T_PillTone;
}): T_PillSurface {
  const isSelected = args.selected === true;
  return args.tone === "accent" || isSelected
    ? E_PillSurface.ACCENT
    : E_PillSurface.CHIP;
}

/**
 * Whether the dimmed disabled treatment applies.
 *
 * A selected pill keeps full contrast even when disabled: dimming it would
 * hide which option is currently chosen.
 */
export function shouldDimDisabledPill(args: {
  disabled?: boolean;
  selected?: boolean;
}): boolean {
  return args.disabled === true && args.selected !== true;
}
